import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Download, Printer, TrendingUp, TrendingDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface FinancialSummary {
  total_revenue: number;
  total_expenses: number;
  net_profit: number;
  total_invoices: number;
  paid_invoices: number;
  pending_invoices: number;
  total_collections: number;
  total_dues: number;
  total_refunds: number;
  total_commissions: number;
}

export default function SummaryReport() {
  const { formatCurrency } = useCurrency();
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadSummary();
  }, [startDate, endDate]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setSummary(getMockSummary());
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('financial_summary_view')
        .select('*')
        .single();

      if (error) throw error;
      setSummary(data);
    } catch (error) {
      console.error('Error loading summary:', error);
      setSummary(getMockSummary());
    } finally {
      setLoading(false);
    }
  };

  const getMockSummary = (): FinancialSummary => {
    return {
      total_revenue: 2500000,
      total_expenses: 1200000,
      net_profit: 1300000,
      total_invoices: 450,
      paid_invoices: 380,
      pending_invoices: 70,
      total_collections: 2100000,
      total_dues: 400000,
      total_refunds: 50000,
      total_commissions: 180000,
    };
  };

  const handleExportCSV = () => {
    if (!summary) return;

    const headers = ['Metric', 'Value'];
    const rows = [
      ['Total Revenue', summary.total_revenue],
      ['Total Expenses', summary.total_expenses],
      ['Net Profit', summary.net_profit],
      ['Total Invoices', summary.total_invoices],
      ['Paid Invoices', summary.paid_invoices],
      ['Pending Invoices', summary.pending_invoices],
      ['Total Collections', summary.total_collections],
      ['Total Dues', summary.total_dues],
      ['Total Refunds', summary.total_refunds],
      ['Total Commissions', summary.total_commissions],
    ];

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `financial-summary-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading || !summary) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const profitMargin = ((summary.net_profit / summary.total_revenue) * 100).toFixed(2);
  const collectionRate = ((summary.total_collections / summary.total_revenue) * 100).toFixed(2);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Summary Report</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(summary.total_revenue)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Expenses</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(summary.total_expenses)}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Net Profit</p>
                <p className={`text-2xl font-bold mt-1 ${summary.net_profit >= 0 ? 'text-teal-600' : 'text-red-600'}`}>
                  {formatCurrency(summary.net_profit)}
                </p>
              </div>
              {summary.net_profit >= 0 ? (
                <TrendingUp className="w-8 h-8 text-teal-600" />
              ) : (
                <TrendingDown className="w-8 h-8 text-red-600" />
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Statistics</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Invoices</span>
                <span className="font-bold text-gray-900">{summary.total_invoices}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Paid Invoices</span>
                <span className="font-bold text-green-600">{summary.paid_invoices}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Pending Invoices</span>
                <span className="font-bold text-yellow-600">{summary.pending_invoices}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Metrics</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Profit Margin</span>
                <span className="font-bold text-teal-600">{profitMargin}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Collection Rate</span>
                <span className="font-bold text-blue-600">{collectionRate}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Commissions</span>
                <span className="font-bold text-purple-600">{formatCurrency(summary.total_commissions)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Collections</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{formatCurrency(summary.total_collections)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Dues</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">{formatCurrency(summary.total_dues)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Refunds</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(summary.total_refunds)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
